import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { money } from "@/lib/format";
import { ConditionRatingCard } from "@/components/ConditionRating";
import { ProductCard } from "@/components/ProductCard";
import { ProductGallery } from "@/components/ProductGallery";
import { LookersInspection } from "@/components/LookersInspection";
import { FramePassport } from "@/components/FramePassport";
import { ProductReviews } from "@/components/ProductReviews";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useBrandLogos } from "@/lib/brandLogos";
import { getFitLabel } from "@/lib/fit";
import { pushRecentlyViewed } from "@/lib/recentlyViewed";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, items } = useCart();
  const { logoFor } = useBrandLogos();
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [lensUpgrade, setLensUpgrade] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lensPrice, setLensPrice] = useState(49);
  const [notFound, setNotFound] = useState(false);
  const [fitReference, setFitReference] = useState(null);

  useEffect(() => {
    if (!user) {
      setFitReference(null);
      return;
    }
    api.get("/account/measurements").then((res) => setFitReference(res.data[0] || null)).catch(() => {});
  }, [user]);

  useEffect(() => {
    api.get(`/products/${slug}`).then((res) => {
      setProduct(res.data);
      pushRecentlyViewed(res.data.id);
      api.get(`/products/${slug}/similar`).then((r) => setSimilar(r.data)).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    api.get("/settings/public").then((res) => setLensPrice(res.data.lens_upgrade_price)).catch(() => {});
  }, []);

  if (notFound) return <div className="container-lookers py-24 text-center text-zinc-900" data-testid="product-not-found">Product not found.</div>;
  if (!product) return <div className="container-lookers py-24 text-center text-zinc-400">Loading…</div>;

  const images = product.images || [];
  const inCart = items.some((i) => i.product_id === product.id);
  const rrpVerified = product.rrp_verified !== false && !!product.original_rrp;
  const savings = rrpVerified ? Math.round(((product.original_rrp - product.selling_price) / product.original_rrp) * 100) : null;
  const isLive = product.status === "live";
  const fitLabel = getFitLabel(product, fitReference);

  const handleAddToCart = () => {
    if (!isLive) return;
    addToCart(product, lensUpgrade, lensPrice);
    toast.success("Added to your bag");
  };

  const handleBuyNow = () => {
    if (!isLive) return;
    if (!inCart) addToCart(product, lensUpgrade, lensPrice);
    navigate("/checkout");
  };

  const toggleWishlist = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      if (saved) {
        await api.delete(`/wishlist/${product.id}`);
      } else {
        await api.post(`/wishlist/${product.id}`);
      }
      setSaved(!saved);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div className="container-lookers py-8 sm:py-12 pb-28 lg:pb-12" data-testid="product-detail-page">
      <div className="text-xs text-neutral-400 mb-8 font-mono uppercase tracking-wide" data-testid="breadcrumb">
        <Link to="/shop" className="hover:text-teal-600">Shop</Link> / <Link to={`/brands/${product.brand_slug}`} className="hover:text-teal-600">{product.brand_name}</Link> / {product.model}
      </div>

      <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
        <div className="lg:col-span-3">
          <ProductGallery key={product.id} images={images} alt={product.model} />
        </div>

        <div className="lg:col-span-2">
          {logoFor(product.brand_slug) ? (
            <img src={logoFor(product.brand_slug)} alt={product.brand_name} className="h-6 w-auto object-contain max-w-[150px] mb-3" data-testid="product-brand-logo" />
          ) : (
            <div className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-2">{product.brand_name}</div>
          )}
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mb-1 text-neutral-900 tracking-tight" data-testid="product-title">
            {product.model}
          </h1>
          {!isLive && (
            <span className="inline-block bg-neutral-900 text-white text-[11px] font-heading font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-2" data-testid="sold-badge">
              Sold
            </span>
          )}
          {product.model_number && (
            <div className="text-xs font-mono text-neutral-400 mb-4" data-testid="product-model-number">Model No. {product.model_number}</div>
          )}
          <div className="flex items-baseline gap-3 mb-3 flex-wrap mt-3">
            <span className="font-mono font-bold text-4xl text-neutral-900" data-testid="product-price">{money(product.selling_price)}</span>
            {rrpVerified && (
              <span className="font-mono text-base text-neutral-400 line-through">RRP {money(product.original_rrp)}</span>
            )}
          </div>
          {savings > 0 && (
            <div className="inline-flex items-center gap-2 bg-teal-600 text-white text-xs font-mono font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-8" data-testid="product-discount-badge">
              Save {money(product.original_rrp - product.selling_price)} · {savings}% Off Retail
            </div>
          )}

          <div className="flex items-center gap-2 mb-6 text-sm" data-testid="pdp-authenticity-checked">
            <ShieldCheck size={16} className="text-teal-600 shrink-0" />
            <span className="font-heading font-bold text-neutral-900">Authenticity Checked</span>
            <Link to="/authenticity" className="text-xs text-teal-700 hover:text-teal-800 underline underline-offset-2" data-testid="pdp-authenticity-guarantee-link">
              Covered by the Lookers Authenticity Guarantee
            </Link>
          </div>

          <div className="mb-6">
            <ConditionRatingCard score={product.condition_score} label={product.condition_label} imperfections={product.imperfections} />
            {product.condition_notes && (
              <p className="text-sm text-neutral-500 mt-3 leading-relaxed" data-testid="condition-written-notes">{product.condition_notes}</p>
            )}
          </div>

          {isLive ? (
            <div className="mb-2 text-[11px] font-mono uppercase tracking-widest text-teal-600 font-bold" data-testid="stock-status">Only One Available — This Exact Pair</div>
          ) : (
            <div className="mb-2 text-[11px] font-mono uppercase tracking-widest text-neutral-500" data-testid="stock-status">This Pair Found Its Next Look</div>
          )}

          {isLive && (
            <div className="border border-neutral-200 rounded-2xl p-5 mb-6 bg-white" data-testid="lens-options">
              <div className="text-[11px] font-mono uppercase tracking-widest mb-3 text-neutral-500 font-bold">Lens Options</div>
              <label className="flex items-center gap-2 mb-2 text-sm cursor-pointer text-neutral-800">
                <input type="radio" checked={!lensUpgrade} onChange={() => setLensUpgrade(false)} className="accent-teal-600" data-testid="lens-frame-only" />
                Frame only — {money(product.selling_price)}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer text-neutral-800">
                <input type="radio" checked={lensUpgrade} onChange={() => setLensUpgrade(true)} className="accent-teal-600" data-testid="lens-add-clear" />
                Add clear non-prescription lenses — +{money(lensPrice)}
              </label>
              <p className="text-xs text-neutral-400 mt-2">Clear non-prescription lenses only. Not prescription eyewear.</p>
            </div>
          )}

          <div className="hidden lg:flex gap-3 mb-8">
            <button
              onClick={handleAddToCart}
              disabled={!isLive || inCart}
              className="flex-1 bg-charcoal hover:bg-teal-600 text-white font-heading font-bold text-xs uppercase tracking-wider py-4 rounded-full disabled:opacity-40 transition-all active:scale-95"
              data-testid="add-to-bag-button"
            >
              {inCart ? "In Your Bag" : isLive ? "Add To Bag" : "Sold"}
            </button>
            {isLive && (
              <button onClick={handleBuyNow} className="flex-1 bg-[#09B8DF] hover:bg-teal-600 text-white font-heading font-bold text-xs uppercase tracking-wider py-4 rounded-full transition-all active:scale-95" data-testid="buy-now-button">
                Buy Now
              </button>
            )}
            <button onClick={() => navigate(`/try-on/${product.id}`)} className="flex-1 border-2 border-neutral-200 hover:border-charcoal hover:text-charcoal text-neutral-700 font-heading font-bold text-xs uppercase tracking-wider py-4 rounded-full transition-all active:scale-95" data-testid="try-them-on-button">
              Try Them On
            </button>
            <button onClick={toggleWishlist} className="w-14 border-2 border-neutral-200 hover:border-teal-600 text-neutral-700 rounded-full flex items-center justify-center transition-colors" aria-label="Save" data-testid="product-wishlist-button">
              <Heart size={16} className={saved ? "fill-rose-500 text-rose-500" : ""} />
            </button>
          </div>

          <div className="lg:hidden flex gap-3 mb-8">
            <button onClick={() => navigate(`/try-on/${product.id}`)} className="flex-1 border-2 border-neutral-200 hover:border-charcoal hover:text-charcoal text-neutral-700 font-heading font-bold text-xs uppercase tracking-wider py-4 rounded-full transition-all active:scale-95" data-testid="try-them-on-button-mobile">
              Try Them On
            </button>
            <button onClick={toggleWishlist} className="w-14 border-2 border-neutral-200 hover:border-teal-600 text-neutral-700 rounded-full flex items-center justify-center transition-colors" aria-label="Save" data-testid="product-wishlist-button-mobile">
              <Heart size={16} className={saved ? "fill-rose-500 text-rose-500" : ""} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm mb-6 font-mono bg-white border border-neutral-200 rounded-2xl p-5 text-neutral-800" data-testid="measurements-table">
            <div><span className="text-neutral-400">Lens width:</span> {product.lens_width}mm</div>
            <div><span className="text-neutral-400">Bridge:</span> {product.bridge_width}mm</div>
            <div><span className="text-neutral-400">Temple:</span> {product.temple_length}mm</div>
            {product.overall_frame_width && <div><span className="text-neutral-400">Overall width:</span> {product.overall_frame_width}mm</div>}
            <div><span className="text-neutral-400">Size:</span> {product.size}</div>
            <div><span className="text-neutral-400">Material:</span> {product.frame_material}</div>
            <div><span className="text-neutral-400">Colour:</span> {product.frame_colour}</div>
            <div><span className="text-neutral-400">Shape:</span> {product.shape}</div>
          </div>
          {fitLabel && (
            <div className={`flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wide mb-6 ${fitLabel.includes("Match") || fitLabel.includes("Similar") ? "text-teal-600" : "text-neutral-500"}`} data-testid="pdp-fit-indicator">
              vs. your saved size ({fitReference.label}): {fitLabel}
            </div>
          )}

          {product.whats_included?.length > 0 && (
            <div className="text-sm text-neutral-600 mb-6" data-testid="whats-included">
              <span className="text-neutral-400 font-mono text-xs uppercase tracking-wide">What's Included: </span>
              {product.whats_included.join(", ")}
              {product.original_case_included && ", Original Case"}
            </div>
          )}

          <div className="mb-8">
            <LookersInspection inspection={product.inspection} />
          </div>

          <div className="mb-8 rounded-2xl bg-[#F8F8F9] border border-neutral-200/70 p-5" data-testid="pdp-authenticity-detail">
            <div className="flex items-start gap-3">
              <ShieldCheck size={18} className="text-teal-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-bold text-sm text-neutral-900 mb-1">Buy preloved with confidence.</div>
                <p className="text-sm text-neutral-500 leading-relaxed mb-2">
                  This frame has been individually checked by Lookers before being offered for sale.
                </p>
                {(() => {
                  const method = product.authentication_method || "Lookers Authentication Check";
                  const isThirdParty = method === "Third-Party Authentication" && product.authentication_provider;
                  return (
                    <div className="text-xs font-mono text-neutral-600" data-testid="pdp-authentication-method">
                      <span className="text-neutral-400 uppercase tracking-wide">Authentication: </span>
                      <span className="text-neutral-900 font-bold">{isThirdParty ? "Independently Authenticated" : method}</span>
                      {isThirdParty && (
                        <span className="block mt-0.5"><span className="text-neutral-400 uppercase tracking-wide">Provider: </span>{product.authentication_provider}</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-neutral-500 border-t border-neutral-100 pt-5">
            <div className="flex items-start gap-2">
              <Truck size={15} className="text-teal-600 shrink-0 mt-0.5" />
              <span>Free tracked shipping on orders over $200 within Australia.</span>
            </div>
            <div className="flex items-start gap-2">
              <RotateCcw size={15} className="text-teal-600 shrink-0 mt-0.5" />
              <span>Genuine problem with your order? We'll make it right under Australian Consumer Law. <Link to="/returns" className="text-teal-700 underline underline-offset-2" data-testid="pdp-returns-link">Returns policy</Link>.</span>
            </div>
            <div className="flex items-start gap-2">
              <ShieldCheck size={15} className="text-teal-600 shrink-0 mt-0.5" />
              <span>Every frame is checked for authenticity before listing.</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-16 max-w-xl">
        <FramePassport product={product} />
      </div>

      <ProductReviews productId={product.id} />

      {similar.length > 0 && (
        <section className="mt-24" data-testid="similar-products-section">
          <h2 className="font-heading font-extrabold text-3xl mb-8 text-neutral-900 tracking-tight">
            {isLive ? "Similar Look" : "Find Something Similar"}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-14">
            {similar.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {isLive && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 p-3 flex items-center gap-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]" data-testid="mobile-sticky-buy-bar">
          <div className="font-mono font-bold text-lg text-neutral-900 shrink-0" data-testid="mobile-sticky-price">{money(product.selling_price)}</div>
          <button
            onClick={handleAddToCart}
            disabled={inCart}
            className="flex-1 bg-charcoal text-white font-heading font-bold text-xs uppercase tracking-wider py-3.5 rounded-full disabled:opacity-40 active:scale-95 transition-all"
            data-testid="mobile-sticky-add-to-bag"
          >
            {inCart ? "In Bag" : "Add To Bag"}
          </button>
          <button onClick={handleBuyNow} className="flex-1 bg-[#09B8DF] text-white font-heading font-bold text-xs uppercase tracking-wider py-3.5 rounded-full active:scale-95 transition-all" data-testid="mobile-sticky-buy-now">
            Buy Now
          </button>
        </div>
      )}
    </div>
  );
}
